import AppButton from '@nxsys/components/AppButton';
import * as Icons from '@nxsys/components/AppIconSvg';
import AppCard from '@nxsys/components/AppCard';
import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { aepnActions } from '@nxsys/store/slices/aepn/reducer/aepnSlice';
import { GetAEPNListParams } from '@nxsys/store/slices/aepn/types';
import { IAepn } from '@nxsys/types/models/aepn';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import { csvConfigExport } from '@nxsys/utils/export';
import { showToast } from '@nxsys/utils/toast';
import { ExportToCsv } from 'export-to-csv';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionsHeader } from '../components/ActionsHeader';
import AEPNDetailModal from '../components/AEPNDetailModal';
import UpdateAEPNModal from '../components/UpdateAEPNModal';
import AEPNUploadTab from './AEPNUploadTab';
import { getColumns } from './columns';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';

type Props = {
  cbAfterAction?: () => void;
  isDashboard?: boolean;
};

const AEPNListing: React.FC<Props> = ({ cbAfterAction, isDashboard }) => {
  const { messages } = useTypedIntl();

  const tabs = [
    { tab: 'AEPN Updates', key: 'aepnUpdates' },
    { tab: 'Errors', key: 'errors' },
    { tab: 'AEPN Upload', key: 'aepnUpload' },
  ];

  const dispatch = useDispatch<AppDispatch>();
  const { aepnList } = useSelector((state: AppState) => state.aepnStore);
  const { loading, list, pagination, queryParams } = aepnList;
  const { isBureauEmployer, user } = useSelector(
    (state: AppState) => state.appStore,
  );

  const [selectedTaxYear, setSelectedTaxYear] = useState<number>(
    getTaxWeekTaxYear().taxYear,
  );
  // A ref, not state: the filter modal fetches with the new employers itself,
  // so changing them must not trigger a second fetch from the effect below
  const selectedCompanyIdsRef = useRef<string[]>([]);
  const [selectedRows, setSelectedRows] = useState<IAepn[]>([]);
  const [activeTab, setActiveTab] = useState(tabs[0]?.key);
  const [detailModal, setDetailModal] = useState({
    open: false,
    data: null,
  });
  const [updateAEPNModal, setUpdateAEPNModal] = useState({
    open: false,
  });
  const [keyRefreshTab, setKeyRefreshTab] = useState(0);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle prefix="AEPN Updates" separator="" title={''} />
    );
  }, [messages]);

  const renderRightContent = () => {
    if (activeTab === 'aepnUpload') {
      return (
        <div className="flex items-center gap-2">
          <AppButton
            type="primary"
            name="Upload AEPN"
            onClick={() => setUploadModalOpen(true)}
          />
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <AppButton
          variant="outlined"
          name="Update AEPN"
          onClick={toggleUpdateAEPNModal}
        />
        <AppButton
          variant="outlined"
          type="primary"
          name={messages['rpn.exportSelected']}
          leftIcon={<Icons.IconArrowExport />}
          onClick={handleExportSelected}
        />
        <AppButton
          type="primary"
          name={messages['rpn.exportAll']}
          leftIcon={<Icons.IconArrowExport color="#fff" />}
          onClick={handleExportAll}
        />
      </div>
    );
  };

  // Read through a ref: handleFetchData must stay stable for the effect below,
  // but a page change has to keep the filters the last request applied
  const queryParamsRef = useRef(queryParams);
  queryParamsRef.current = queryParams;

  const handleFetchData = useCallback((params: GetAEPNListParams) => {
    const merged: GetAEPNListParams = { ...queryParamsRef.current, ...params };
    merged.companyIds = resolveFilterCompanyIds(
      merged.companyIds,
      isBureauEmployer,
      user?.companyUserIds,
    );
    // Only a tab switch says which list the dashboard shows; a page change or
    // the filter modal keeps the flags the last request used
    if (isDashboard && params.isError !== undefined) {
      merged.isErrorLatestToday = params.isError ? true : undefined;
      merged.isLatestToday = params.isError ? undefined : true;
      merged.isError = undefined;
    }
    dispatch(aepnActions.getAEPNList(merged));
  }, [isBureauEmployer, user?.companyUserIds, isDashboard, dispatch]);

  useEffect(() => {
    if (activeTab === 'aepnUpload') return;
    handleFetchData({
      page: 0,
      size: 20,
      taxYear: selectedTaxYear,
      isError: activeTab === 'errors',
      ...(selectedCompanyIdsRef.current.length > 0 && {
        companyIds: selectedCompanyIdsRef.current,
      }),
    });
  }, [activeTab, selectedTaxYear, handleFetchData]);

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleExportExcel = (data: IAepn[]) => {
    const dataExport = data.map((item) => {
      const {
        employeePPSN,
        employmentID,
        aepnNumber,
        enrolmentStatus,
        eeContributionRate,
        erContributionRate,
        aepnEnrolmentDate,
        aepnChangeEvent,
        aepnIssueDate,
        employerRegistrationNumber,
        aepnDownloadDateTime,
        createdAt,
        subcontractor,
      } = item;

      return {
        dateReceived: createdAt ? moment(createdAt).format('DD/MM/YYYY') : '',
        aepnDownloadDateTime: aepnDownloadDateTime ? moment(aepnDownloadDateTime).format('DD/MM/YYYY') : '',
        aepnIssueDate: aepnIssueDate ? moment(aepnIssueDate).format('DD/MM/YYYY') : '',
        employeeId: subcontractor?.id || item.subcontractorId || '',
        employeeName: `${subcontractor?.forename || '_'} ${subcontractor?.surname || '_'}`,
        employerRef: subcontractor?.agencyRef || '',
        employeePPSN: employeePPSN || '',
        aepnNumber,
        enrolmentStatus: enrolmentStatus || '',
        aepnEnrolmentDate: aepnEnrolmentDate ? moment(aepnEnrolmentDate).format('DD/MM/YYYY') : '',
        aepnChangeEvent: aepnChangeEvent || '',
        eeContributionRate: eeContributionRate ? eeContributionRate : 0.00,
        erContributionRate: erContributionRate ? erContributionRate : 0.00,
        employmentID: employmentID !== null ? String(employmentID) : '',
        employerRegistrationNumber: employerRegistrationNumber || '',
      };
    });
    const headersExport = [
      'Date Received',
      'AEPN Download Date',
      'AEPN Issue Date',
      'Employee ID',
      'Employee Name',
      'Employer Ref',
      'PPS Number',
      'AEPN Number',
      'Enrolment Status',
      'AEPN Enrolment Date',
      'AEPN Change Event',
      'EE Contribution Rate',
      'ER Contribution Rate',
      'Employment ID',
      'Employer Registration Number',
    ];
    const exportName = csvConfigExport(`AEPN-EXPORT`, headersExport);
    const csvExporter = new ExportToCsv(exportName);
    csvExporter.generateCsv(dataExport);
  };

  const handleExportSelected = () => {
    if (!selectedRows || selectedRows.length === 0) {
      showToast({
        type: 'warning',
        message: messages['common.selectAtLeastOneToExport'],
      });
      return;
    }
    handleExportExcel(selectedRows);
  };

  const handleExportAll = async () => {
    if (!pagination.totalDocs) {
      showToast({ type: 'warning', message: messages['common.noDataToExport'] });
      return;
    }
    const res = await dispatch(
      aepnActions.getAEPNList({
        ...queryParams,
        page: 0,
        size: pagination.totalDocs,
        search: undefined,
        aepnIssueDate: undefined,
        aepnDownloadDateTime: undefined,
        aepnEnrolmentDate: undefined,
        enrolmentStatus: undefined,
      }),
    ).unwrap();
    handleExportExcel(res.data);
  };

  const toggleDetailModal = () =>
    setDetailModal({ ...detailModal, open: !detailModal.open });

  const handleOpenDetailModal = (data: any) => {
    setDetailModal({
      open: true,
      data,
    });
  };

  const toggleUpdateAEPNModal = () =>
    setUpdateAEPNModal({ ...updateAEPNModal, open: !updateAEPNModal.open });

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setKeyRefreshTab((prev) => prev + 1);
  };

  const handleTaxYearChange = (filters: { taxYear: number | undefined }) => {
    if (filters.taxYear !== undefined) {
      setSelectedTaxYear(filters.taxYear);
    }
  };

  const handleEmployerChange = (companyIds: string[]) => {
    selectedCompanyIdsRef.current = companyIds;
  };

  const isUploadTab = activeTab === 'aepnUpload';

  const renderFilters = () => {
    return (
      <AppsHeader>
        <div className="flex justify-between flex-wrap w-full gap-4">
          <ActionsHeader
            onFilter={handleFetchData}
            hideAdvancedFilter={isUploadTab}
            onChangeCompanyIds={handleEmployerChange}
          />
          <div className="flex flex-wrap gap-4 items-end">
            <TaxYearSelect
              initValue={selectedTaxYear}
              onFilter={handleTaxYearChange}
            />
          </div>
        </div>
      </AppsHeader>
    );
  };

  const renderContent = () => {
    return (
      <>
        {isDashboard && (
          <div className="flex justify-end px-4 pt-2">
            {renderRightContent()}
          </div>
        )}
        {renderFilters()}
        <AppCardTabListContainer
          tabList={tabs}
          activeTab={activeTab}
          handleTabChange={handleTabChange}
          key={keyRefreshTab}
          {...isDashboard && { className: '!border-none' }}
        >
          <AppsContent>
            {activeTab === 'aepnUpload' ? (
              <AEPNUploadTab
                isUploadModalOpen={uploadModalOpen}
                onUploadModalClose={() => setUploadModalOpen(false)}
                taxYear={selectedTaxYear}
              />
            ) : (
              <AppTableContainer
                rowKey="id"
                data={list ?? []}
                loading={false}
                page={pagination.current}
                total={pagination.totalDocs}
                pageSize={pagination.pageSize}
                scroll={{ x: 4000, y: 700 }}
                titleEmpty={messages['rpn.empty']}
                columns={getColumns({
                  messages,
                  isErrorTab: activeTab === tabs[1]?.key,
                  onClickView: handleOpenDetailModal,
                })}
                onChangePagination={onChangePagination}
                rowSelection={{
                  type: 'checkbox',
                  onChange: (_: React.Key[], selectedRows: any[]) => {
                    setSelectedRows(selectedRows);
                  },
                }}
              />
            )}
          </AppsContent>
        </AppCardTabListContainer>
        <AEPNDetailModal
          isOpen={detailModal.open}
          toggle={toggleDetailModal}
          data={detailModal.data}
        />
        <UpdateAEPNModal
          isOpen={updateAEPNModal.open}
          toggle={toggleUpdateAEPNModal}
          cbAfterAction={cbAfterAction}
        />
      </>
    );
  };

  if (isDashboard) {
    return renderContent();
  }

  return (
    <AppsContainer
      fullView
      type="bottom"
      loading={loading}
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <AppCard>
        {renderContent()}
      </AppCard>
    </AppsContainer>
  );
};

export default AEPNListing;
